/**
 * Admin notification → route resolver (epic #946, P2).
 *
 * The header bell used a chain of `if (item.type == '...')` blocks that
 * navigated only a handful of types and silently did nothing for the rest —
 * even though they carry the `eventId` needed to route. This is the pure,
 * tested mapping that replaces that chain: type → { path, state }, or `null`
 * when there's no destination (the caller then just marks-read/closes).
 *
 * Most admin notices open the job's positions page on a specific tab:
 *   0 quote · 2 bids/contractors · 3 confirmations · 4 completed · 6 invoices
 */

export interface AdminNotificationRoute {
  path: string;
  state?: Record<string, unknown>;
}

export interface AdminNotificationItem {
  type?: string | null;
  subType?: string | null;
  metaData?: unknown;
  [key: string]: unknown;
}

function positiveId(value: unknown): number | null {
  if (typeof value !== 'number' &&
      (typeof value !== 'string' || !/^[1-9]\d*$/.test(value))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** Parse an admin notification's metaData (stringified JSON or object). */
export function parseAdminMetaData(
  metaData: unknown
): Record<string, unknown> {
  if (metaData == null) return {};
  if (typeof metaData === 'object') return metaData as Record<string, unknown>;
  if (typeof metaData === 'string' && metaData.trim() !== '') {
    try {
      const parsed = JSON.parse(metaData);
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  return {};
}

/** Positions-page tab index per admin notification type. */
const POSITIONS_TAB: Record<string, string> = {
  quote: '0',
  'bid-created': '2',
  'manual-added-contractor': '2',
  'bid-quick-accepted': '2',
  'shift-confirmed': '3',
  // Assignment / move / shift-change decisions are confirmation-flow events;
  // they land on the confirmations tab. Tab choice is best-effort — confirm in
  // the admin QA pass (see documentation/qa/notification-deeplink-on-device.md).
  'assignment-approved': '3',
  'assignment-declined': '3',
  'move-approved': '3',
  'move-declined': '3',
  shift_change_request: '3',
  'shift-completed': '4',
  'invoice-submitted': '6',
};

export function resolveAdminNotificationRoute(
  item: AdminNotificationItem,
  currentCompanyId?: unknown
): AdminNotificationRoute | null {
  const type = item?.type;
  if (!type) return null;

  if (type === 'chat') {
    return { path: '/dashboards/messenger', state: { data: item } };
  }

  if (type === 'event' && item.subType === 'company_scheduling') {
    const meta = parseAdminMetaData(item.metaData);
    const companyId = positiveId(meta.companyId);
    const eventId = positiveId(meta.eventId);
    if (!companyId || !eventId ||
        !positiveId(meta.assignmentId) || !positiveId(meta.shiftId) ||
        !positiveId(meta.workerAccountId) ||
        companyId !== positiveId(currentCompanyId) ||
        (item.companyId != null && companyId !== positiveId(item.companyId))) return null;
    return { path: `/dashboards/jobTracker/positions/${eventId}?tab=bookings` };
  }

  const tab = POSITIONS_TAB[type];
  if (tab === undefined) return null;

  const meta = parseAdminMetaData(item.metaData);
  const eventId = meta.eventId;
  if (eventId == null) return null;

  if (type === 'bid-created') {
    if (!/^[1-9]\d*$/.test(String(eventId)) || !Number.isSafeInteger(Number(eventId))) return null;
    return { path: `/dashboards/jobTracker/positions/${eventId}/bids` };
  }

  return {
    path: `/dashboards/jobTracker/positions/${eventId}`,
    state: { title: meta.title, tab },
  };
}
